// Dashboard
import Dashboard from "./features/dashboard/components/Dashboard";

// Workflow Step 1: Requests
import RequestsPage from "./features/requests/components/RequestsPage";
import RequestDetailPage from "./features/requests/components/RequestDetailPage";
import CreateRequestPage from "./features/requests/components/CreateRequestPage";

// Workflow Step 2: Quotes
import QuotesPage from "./features/quotes/components/QuotesPage";
import QuotesDetailPage from "./features/quotes/components/QuotesDetailPage";
import CreateQuotePage from "./features/quotes/components/CreateQuotePage";
// import UpdateQuotePage  from './features/quotes/components/UpdateQuotePage';

// Workflow Step 3: Jobs
import JobsPage from "./features/jobs/components/JobsPage";
import JobDetailPage from "./features/jobs/components/JobDetailPage";
import CreateJobPage from "./features/jobs/components/CreateJobPage";

// Workflow Step 4: Invoices
import { InvoicesPage, InvoiceDetails } from './features/invoices/components';

// Relationship: Properties
import PropertiesPage from "./features/properties/components/PropertiesPage";
import PropertyDetailPage from "./features/properties/components/PropertyDetailPage";
import CreatePropertyPage from "./features/properties/components/CreatePropertyPage";

// Relationship: Businesses
import BusinessesPage from "./features/service-providers/components/BusinessesPage";
import BusinessDetailPage from "./features/service-providers/components/BusinessDetailPage";
import CreateBusinessPage from "./features/service-providers/components/CreateBusinessPage";
import { BusinessDataProvider } from "./features/service-providers/components";

// Relationship: Services
import ServicesPage from "./features/services/components/ServicesPage";
import ServiceDetailsPage from "./features/services/components/ServiceDetailsPage";

// Relationship: Customers
import CustomersPage from "./features/customers/components/CustomersPage";
import CustomerDetailPage from "./features/customers/components/CustomerDetailPage";
import CreateCustomerPage from "./features/customers/components/CreateCustomerPage";

// Relationship: Employees
import EmployeesPage from "./features/employees/components/EmployeesPage";
import EmployeeDetailPage from "./features/employees/components/EmployeeDetailPage";
import CreateEmployeePage from "./features/employees/components/CreateEmployeePage";

// Relationship: Schedule
import SchedulePage from "./features/schedule/components/SchedulePage";

// Client Portal
import ClientLogin from "./features/authentication/components/ClientLogin";

// Admin
import AdminRBAC from "./features/admin/components/AdminRBAC";
import UserManagement from "./features/admin/components/UserManagement";
import Settings from "./features/admin/components/Settings";

// Growth Ops
import GrowthOpsPage from "./features/growth-ops/components/GrowthOpsPage";
import AuditLogPage from "./features/growth-ops/components/AuditLogPage";
import PerNetworkReportPage from "./features/growth-ops/components/PerNetworkReportPage";
import ObservabilityPage from "./features/growth-ops/components/ObservabilityPage";
import { GrowthOpsProvider } from "./features/growth-ops/components";

// Authentication
import LoginClerk from "./features/authentication/components/LoginClerk";
import RegisterClerk from "./features/authentication/components/RegisterClerk";
import ProtectedRoute from "./components/ProtectedRoute";
import Profile from "./features/users/components/Profile";
import ResetPassword from "./features/authentication/components/ResetPassword";
import AuthCallback from "./features/authentication/components/AuthCallback";

// Data Providers
import { InvoicesDataProvider } from './features/invoices/context/InvoicesContext';
import { PropertiesDataProvider } from "./features/properties/context/PropertiesContext";
import { CustomerDataProvider } from "./features/customers/context/CustomersContext";
import { EmployeesDataProvider } from "./features/employees/context/EmployeesContext";
import { ServicesDataProvider } from "./features/services/context/ServicesContext";
import { QuoteDataProvider } from "./features/quotes/context/QuotesContext";
import { JobsProviderWrapper } from "./features/jobs/components/JobsProviderWrapper";

// Other
import Layout from "./components/Layout";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { useErrorTracking } from "./hooks/usePerformanceTracking";

function App() {
  // Enable global error tracking
  useErrorTracking();

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Routes>
          <Route path="/sign-in" element={<LoginClerk />} />
          <Route path="/sign-up" element={<RegisterClerk />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/client-login" element={<ClientLogin />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <ServicesDataProvider>
                      <BusinessDataProvider>
                        <JobsProviderWrapper>
                          <EmployeesDataProvider>
                            <QuoteDataProvider>
                              <Dashboard />
                            </QuoteDataProvider>
                          </EmployeesDataProvider>
                        </JobsProviderWrapper>
                      </BusinessDataProvider>
                    </ServicesDataProvider>
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <CustomersPage />
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/:customerId"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <ServicesDataProvider>
                      <BusinessDataProvider>
                        <JobsProviderWrapper>
                          <PropertiesDataProvider>
                            <CustomerDetailPage />
                          </PropertiesDataProvider>
                        </JobsProviderWrapper>
                      </BusinessDataProvider>
                    </ServicesDataProvider>
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <CreateCustomerPage />
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/requests/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <ServicesDataProvider>
                      <PropertiesDataProvider>
                        <Routes>
                          <Route index element={<RequestsPage />} />
                          <Route path="create" element={<CreateRequestPage />} />
                          <Route
                            path=":requestId"
                            element={<RequestDetailPage />}
                          />
                        </Routes>
                      </PropertiesDataProvider>
                    </ServicesDataProvider>
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <QuoteDataProvider>
                      <BusinessDataProvider>
                        <Routes>
                          <Route index element={<QuotesPage />} />
                          <Route path="create" element={<CreateQuotePage />} />
                          <Route
                            path="update/:quoteId"
                            element={<CreateQuotePage />}
                          />
                          <Route path=":quoteId" element={<QuotesDetailPage />} />
                        </Routes>
                      </BusinessDataProvider>
                    </QuoteDataProvider>
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <PropertiesDataProvider>
                      <PropertiesPage />
                    </PropertiesDataProvider>
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties/:propertyId"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <ServicesDataProvider>
                      <BusinessDataProvider>
                        <JobsProviderWrapper>
                          <PropertiesDataProvider>
                            <PropertyDetailPage />
                          </PropertiesDataProvider>
                        </JobsProviderWrapper>
                      </BusinessDataProvider>
                    </ServicesDataProvider>
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <ServicesDataProvider>
                      <BusinessDataProvider>
                        <JobsProviderWrapper>
                          <PropertiesDataProvider>
                            <CreatePropertyPage />
                          </PropertiesDataProvider>
                        </JobsProviderWrapper>
                      </BusinessDataProvider>
                    </ServicesDataProvider>
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <ServicesDataProvider>
                      <BusinessDataProvider>
                        <JobsProviderWrapper>
                          <EmployeesDataProvider>
                            <PropertiesDataProvider>
                              <Outlet />
                            </PropertiesDataProvider>
                          </EmployeesDataProvider>
                        </JobsProviderWrapper>
                      </BusinessDataProvider>
                    </ServicesDataProvider>
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          >
            <Route index element={<JobsPage />} />
            <Route path="create" element={<CreateJobPage />} />
            <Route path=":jobId" element={<JobDetailPage />} />
          </Route>
          <Route
            path="/schedule"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <ServicesDataProvider>
                      <BusinessDataProvider>
                        <JobsProviderWrapper>
                          <EmployeesDataProvider>
                            <SchedulePage />
                          </EmployeesDataProvider>
                        </JobsProviderWrapper>
                      </BusinessDataProvider>
                    </ServicesDataProvider>
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <ServicesDataProvider>
                      <BusinessDataProvider>
                        <JobsProviderWrapper>
                          <EmployeesDataProvider>
                            <EmployeesPage />
                          </EmployeesDataProvider>
                        </JobsProviderWrapper>
                      </BusinessDataProvider>
                    </ServicesDataProvider>
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/create"
            element={
              <ProtectedRoute>
                <Layout>
                  <EmployeesDataProvider>
                    <CreateEmployeePage />
                  </EmployeesDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/:employeeId"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <ServicesDataProvider>
                      <BusinessDataProvider>
                        <JobsProviderWrapper>
                          <EmployeesDataProvider>
                            <EmployeeDetailPage />
                          </EmployeesDataProvider>
                        </JobsProviderWrapper>
                      </BusinessDataProvider>
                    </ServicesDataProvider>
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-providers"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <ServicesDataProvider>
                      <BusinessDataProvider>
                        <JobsProviderWrapper>
                          <BusinessesPage />
                        </JobsProviderWrapper>
                      </BusinessDataProvider>
                    </ServicesDataProvider>
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-providers/create"
            element={
              <ProtectedRoute>
                <Layout>
                  <BusinessDataProvider>
                    <CreateBusinessPage />
                  </BusinessDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-providers/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <BusinessDataProvider>
                    <BusinessDetailPage />
                  </BusinessDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/services"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServicesDataProvider>
                    <ServicesPage />
                  </ServicesDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/services/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServicesDataProvider>
                    <ServiceDetailsPage />
                  </ServicesDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/invoices" element={
            <ProtectedRoute>
              <Layout>
                <InvoicesDataProvider>
                  <InvoicesPage />
                </InvoicesDataProvider>
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/invoices/:id" element={
            <ProtectedRoute>
              <Layout>
                <InvoicesDataProvider>
                  <InvoiceDetails />
                </InvoicesDataProvider>
              </Layout>
            </ProtectedRoute>
          } />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/adminrbac"
            element={
              <ProtectedRoute>
                <Layout>
                  <AdminRBAC />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDataProvider>
                    <ServicesDataProvider>
                      <BusinessDataProvider>
                        <JobsProviderWrapper>
                          <PropertiesDataProvider>
                            <EmployeesDataProvider>
                              <Settings />
                            </EmployeesDataProvider>
                          </PropertiesDataProvider>
                        </JobsProviderWrapper>
                      </BusinessDataProvider>
                    </ServicesDataProvider>
                  </CustomerDataProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Growth Ops */}
          <Route
            path="/growth-ops/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <GrowthOpsProvider>
                    <Routes>
                      <Route index element={<GrowthOpsPage />} />
                      <Route path="audit" element={<AuditLogPage />} />
                      <Route path="per-network/:businessId" element={<PerNetworkReportPage />} />
                      <Route path="observability" element={<ObservabilityPage />} />
                    </Routes>
                  </GrowthOpsProvider>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </Router>
  );
}
export default App;
